import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Form, Modal, Space, Spin, Result, message } from 'antd';
import { HeaderCard } from './HeaderCard';
import { LeadEditForm } from './LeadEditForm';
import { personToEditInput } from './mapLeadDetail';
import { AiSummary } from './AiSummary';
import { OpportunitiesPanel } from './OpportunitiesPanel';
import { TasksPanel } from './TasksPanel';
import { NotesPanel } from './NotesPanel';
import { DiscoveryPanel } from './DiscoveryPanel';
import { CardImagesPanel, type LeadDetailPhotosApi } from './CardImagesPanel';
import { ContactEnrichmentPanel, type LeadEnrichmentApi } from './ContactEnrichmentPanel';
import { BadgeQrPanel } from './BadgeQrPanel';
import { TextEditModal } from './TextEditModal';
import type { DiscoveryAnswerBody } from './ResponseEditModal';
import { RequirementsChecklist } from '../leadRules/RequirementsChecklist';
import { notesCounterState } from '../leadRules/notesCounter';
import { space, useNarrowViewport } from '@viq-admin/ui';
import type {
  DiscoveryResponse,
  LeadDetail,
  PersonEditInput,
  RawnoteEditFields,
  RawnoteEdited,
} from './types';

/**
 * Shell-provided data access for the shared Lead Detail body. Each SPA wires
 * this to its own plane's endpoints (customer `/v1/admin/*` vs super-admin
 * `/v1/superadmin/*`). `fetchLeadDetail` returns the ALREADY-mapped
 * `LeadDetail` (shell calls `mapLeadDetail` on the raw payload).
 */
export interface LeadDetailApi {
  fetchLeadDetail: (id: string) => Promise<LeadDetail>;
  archiveLead?: (id: string) => Promise<void>;
  restoreLead?: (id: string) => Promise<void>;
  /**
   * OPTIONAL edit closure. Present only when the signed-in role may edit
   * (portal: company_admin; console: super_admin). Absent → no Edit button.
   * Returns the updated person subset (incl. serverVersion) so the body can
   * merge it without a full refetch.
   */
  updateLead?: (
    id: string,
    fields: PersonEditInput,
    expectedVersion: number,
  ) => Promise<LeadDetail['person']>;
  fetchDiscovery: (personId: string) => Promise<DiscoveryResponse | null>;
  /**
   * OPTIONAL card-image access. The super-admin console supplies this; the
   * customer portal supplies it only to a company_admin whose company has
   * card images on. Absent → no Card images panel. Do not make it required.
   */
  photos?: LeadDetailPhotosApi;
  /**
   * OPTIONAL contact-enrichment access. The super-admin console supplies
   * this; the customer portal deliberately does not. Do not make it
   * required.
   */
  enrichment?: LeadEnrichmentApi;
  /**
   * OPTIONAL note edit closure. Present only when the signed-in role may edit
   * (portal: company_admin; console: `update Lead`, which per the leads-only
   * carve-out INCLUDES super_admin_readonly). Absent → notes render read-only.
   */
  updateNote?: (
    leadId: string,
    noteId: string,
    fields: RawnoteEditFields,
    expectedVersion: number,
  ) => Promise<RawnoteEdited>;
  /**
   * OPTIONAL note create closure — add a note to the lead, including one that
   * has none (`updateNote` can only correct a note that already exists).
   * Gated exactly like `updateNote`: portal `company_admin`, console `update
   * Lead`. Absent → no "Add note" control renders.
   */
  createNote?: (leadId: string, text: string) => Promise<RawnoteEdited>;
  /** OPTIONAL Discovery write closures, keyed by the QUESTION's conferenceId. */
  updateDiscoveryAnswer?: (
    conferenceId: string, personId: string, questionId: string, body: DiscoveryAnswerBody,
  ) => Promise<void>;
  deleteDiscoveryAnswer?: (
    conferenceId: string, personId: string, questionId: string,
  ) => Promise<void>;
}

export interface LeadDetailBodyProps {
  id: string;
  api: LeadDetailApi;
  /** Hide the Delete/Restore affordances (role-gated by the shell). Default: writable. */
  readOnly?: boolean;
}

export function LeadDetailBody({ id, api, readOnly }: LeadDetailBodyProps) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['leadDetail', id],
    queryFn: () => api.fetchLeadDetail(id),
    enabled: !!id,
  });

  const [editing, setEditing] = useState(false);
  const narrow = useNarrowViewport();
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  // Owned here (not inside LeadEditForm) so "Use this value" from the
  // contact-enrichment panel can push one field into an ALREADY-open form via
  // form.setFieldValue() — an imperative call needs to target the SAME
  // instance the rendered <Form> is bound to. See prefillEnrichedValue below.
  const [editForm] = Form.useForm<PersonEditInput>();

  // Which note is being edited, and in which of its two fields. `field` is what
  // makes the PATCH partial: the Notes modal sends only `text`, the AI Summary
  // modal only `summary`, so neither can blank the other.
  const [noteEdit, setNoteEdit] = useState<
    { noteId: string; field: 'text' | 'summary'; value: string } | null
  >(null);
  const [savingNote, setSavingNote] = useState(false);
  // The "Add note" modal. Separate from `noteEdit` (which is keyed to an
  // existing note) so a draft can never be mistaken for an edit of one.
  const [addingNote, setAddingNote] = useState(false);
  const [savingNewNote, setSavingNewNote] = useState(false);

  // Neither shell keys its `/leads/:id` route element by `id`, so navigating
  // between two leads (e.g. browser back/forward across two already-visited
  // lead pages) can hand this SAME component instance a new `id` without ever
  // unmounting it. Local edit/modal state lives on this instance, not on the
  // query data, so without this reset an in-flight edit for the OLD lead would
  // survive into the new one. (Task 15 dispatch follow-up on a Task 14 defect —
  // see the DiscoveryPanel `key={id}` note below for the other half of this.)
  useEffect(() => {
    setEditing(false);
    setDirty(false);
    setNoteEdit(null);
  }, [id]);

  // Warn on tab close while an edit is in progress with unsaved changes.
  useEffect(() => {
    if (!editing || !dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [editing, dirty]);

  if (isLoading) return <Spin />;
  if (error || !data) return <Result status="404" title="Lead not found" />;

  // A lead has ONE removed state and the UI calls it "Deleted" everywhere,
  // in both the console and the customer portal (ADR 017). The api/endpoint
  // names below still say "archive" — that mismatch is a deliberate, recorded
  // choice, not an oversight: renaming the wire paths would mean coordinating
  // a Function App deploy with two SWA deploys for no user-visible gain.
  // Do not "tidy" it without reading ADR 017.
  const isDeleted = data.person.deletedAt != null;
  const canEdit = !!api.updateLead && !readOnly && !isDeleted;
  // A deleted lead is a tombstone under review: content edits are not a
  // supported operation on either plane, so note/summary/Discovery edit
  // affordances are withheld the same way canEdit withholds "Edit lead"
  // above. Without this, a soft-deleted lead let a note edit SUCCEED (the
  // backend guards only rawnotes.deleted_at, and the note itself isn't
  // deleted) while a Discovery save FAILED with a misleading "Response not
  // found" (the backend guard requires persons.deleted_at IS NULL) — two
  // behaviours on one screen, one of them a confusing 404.
  const canEditNotes = !!api.updateNote && !isDeleted;
  // Adding a note is the same kind of write as editing one, so it follows the
  // same soft-delete rule: a deleted lead is read-only on both planes.
  const canAddNote = !!api.createNote && !isDeleted;
  const canUpdateDiscoveryAnswer = !!api.updateDiscoveryAnswer && !isDeleted;
  const canDeleteDiscoveryAnswer = !!api.deleteDiscoveryAnswer && !isDeleted;

  function handleDelete() {
    if (!api.archiveLead) return;
    const archive = api.archiveLead;
    Modal.confirm({
      title: 'Delete lead',
      content:
        'This lead will be hidden from the active leads list. You can restore it from the Deleted tab on the Batch screen.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await archive(id);
          message.success('Lead deleted.');
          await refetch();
        } catch (err: unknown) {
          message.error((err as { message?: string }).message ?? 'Delete failed');
        }
      },
    });
  }

  async function handleRestore() {
    if (!api.restoreLead) return;
    try {
      await api.restoreLead(id);
      message.success('Lead restored.');
      await refetch();
    } catch (err: unknown) {
      message.error((err as { message?: string }).message ?? 'Restore failed');
    }
  }

  function startEdit() {
    // Explicit, imperative reseed rather than relying on <Form>'s passive
    // `initialValues` prop: this form instance now outlives any single
    // LeadEditForm mount (see prefillEnrichedValue below), so a value left
    // over from a cancelled prior session must never leak into a fresh one.
    if (data) editForm.setFieldsValue(personToEditInput(data.person));
    setDirty(false);
    setEditing(true);
  }

  // "Use this value" from the contact-enrichment panel. Sets ONLY the draft
  // — it must never call updateLead itself; the operator still reviews and
  // hits the form's own Save. Not already editing → seed the whole form from
  // the current person first (same as a fresh "Edit lead"), then the target
  // field, so no OTHER field silently reverts to blank while the ONE
  // enriched field is being fast-tracked in alongside it. Already editing →
  // touch only the target field, so an in-progress edit to other fields
  // survives untouched.
  function prefillEnrichedValue(field: 'email' | 'phone', value: string) {
    if (!data) return;
    if (!editing) editForm.setFieldsValue(personToEditInput(data.person));
    editForm.setFieldValue(field, value);
    setDirty(true);
    setEditing(true);
  }

  function cancelEdit() {
    if (dirty) {
      Modal.confirm({
        title: 'Discard changes?',
        content: 'Your edits to this lead will be lost.',
        okText: 'Discard',
        okButtonProps: { danger: true },
        onOk: () => {
          setDirty(false);
          setEditing(false);
        },
      });
      return;
    }
    setEditing(false);
  }

  async function handleSave(fields: PersonEditInput) {
    if (!api.updateLead || !data) return;
    setSaving(true);
    try {
      await api.updateLead(id, fields, data.person.serverVersion ?? 0);
      message.success('Lead updated.');
      setDirty(false);
      setEditing(false);
      await refetch();
    } catch (err: unknown) {
      const e = err as { statusCode?: number; message?: string };
      if (e.statusCode === 409) {
        message.warning('This lead changed since you opened it. Reloading the latest version.');
        setDirty(false);
        setEditing(false);
        await refetch();
      } else {
        message.error(e.message ?? 'Update failed');
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleCreateNote(value: string) {
    if (!api.createNote) return;
    setSavingNewNote(true);
    try {
      await api.createNote(id, value);
      message.success('Note added.');
      setAddingNote(false);
      await refetch();
    } catch (err: unknown) {
      const e = err as { statusCode?: number; message?: string };
      // Keep the modal open on failure so what the operator typed is not lost.
      message.error(e.message ?? 'Could not add the note');
    } finally {
      setSavingNewNote(false);
    }
  }

  async function handleSaveNote(value: string) {
    if (!api.updateNote || !data || !noteEdit) return;
    // Re-read the version from the CURRENT query data, not from a value captured
    // when the modal opened — a refetch may have advanced it in between.
    const note = data.rawnotes.find((n) => n.id === noteEdit.noteId);
    if (!note) return;
    setSavingNote(true);
    try {
      // A cleared summary must be sent as `null`, not `''`: AiSummary selects
      // which note to display with `summary != null`, so an empty-string
      // summary would render as a permanently blank (but still "present")
      // card instead of the "No AI summary yet" empty state — and would keep
      // masking any OLDER note that has a real summary. `text` is NOT NULL on
      // the notes table and empty text is already blocked by the modal's
      // `canSave` guard, so this conversion applies to `summary` only.
      const fields: RawnoteEditFields =
        noteEdit.field === 'text'
          ? { text: value }
          : { summary: value.trim() === '' ? null : value };
      await api.updateNote(id, noteEdit.noteId, fields, note.serverVersion);
      message.success('Note updated.');
      setNoteEdit(null);
      await refetch();
    } catch (err: unknown) {
      const e = err as { statusCode?: number; message?: string };
      if (e.statusCode === 409) {
        message.warning('This lead changed since you opened it. Reloading the latest version.');
        setNoteEdit(null);
        await refetch();
      } else {
        // Keep the modal open so the operator's typing is not thrown away.
        message.error(e.message ?? 'Update failed');
      }
    } finally {
      setSavingNote(false);
    }
  }

  // REVERSES a prior decision ("no capture-timeline rendering: the AI Summary
  // distills the same content"). Two things broke that premise: `summary` is
  // null until enrichment runs, so for most leads the AI Summary distilled
  // nothing and the capture was simply unreadable; and the lead-move feature
  // now hard-deletes discovery_responses after writing their content into a
  // system rawnote, which makes that rawnote the only surviving copy. A tool
  // that performs the deletion has to be able to show the replacement.
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: space.lg }}>
      {!readOnly && !editing && (
        <Space style={{ justifyContent: 'flex-end' }}>
          {canEdit && <Button onClick={startEdit}>Edit lead</Button>}
          {isDeleted
            ? api.restoreLead && <Button onClick={handleRestore}>Restore lead</Button>
            : api.archiveLead && (
                <Button danger onClick={handleDelete}>
                  Delete lead
                </Button>
              )}
        </Space>
      )}

      {editing ? (
        <LeadEditForm
          form={editForm}
          initial={data.person}
          submitting={saving}
          onSubmit={handleSave}
          onCancel={cancelEdit}
          onDirtyChange={setDirty}
        />
      ) : (
        <HeaderCard detail={data} />
      )}

      {/* Company lead rules (spec 2026-09-14 §3.4): what this lead is still
          missing. Hidden when the company's rules are off (null), on an older
          backend (undefined), and once the lead has been sent — sent leads are
          never flagged. It stays visible while editing, where it's most useful. */}
      {data.completeness && !data.crmSentAt && (
        <RequirementsChecklist completeness={data.completeness} />
      )}

      {/* `key`: ContactEnrichmentPanel owns its own local state (`actionResult`
          — a suppressed/thin banner, `attempted`) with no reset tied to
          `personId`. Since neither shell keys the `/leads/:id` route by id,
          this component can be reused across a lead change (see the
          `id`-reset effect above) — without a key here, a 409-suppressed
          "Re-run anyway" banner left showing on the old lead would survive
          into the new one, and clicking it would force-enrich the WRONG
          lead. The key forces a clean unmount/remount on every lead change,
          same technique as DiscoveryPanel below — but NOT the same literal
          key string: both are siblings under the same parent element here,
          and React requires sibling keys to be unique regardless of
          component type, so reusing DiscoveryPanel's bare `id` verbatim
          collides with it ("two children with the same key") and silently
          breaks the remount for one of the two. */}
      {api.enrichment && (
        <ContactEnrichmentPanel key={`enrichment-${id}`} personId={id} api={api.enrichment} onUseValue={prefillEnrichedValue} />
      )}
      <CardImagesPanel photos={data.rawphotos} api={api.photos} editing={editing} />
      <BadgeQrPanel cardQr={data.person.cardQr} />

      {/* Side by side on a desktop; stacked on a phone, where half the width
          is too narrow to read either. */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: narrow ? 'minmax(0, 1fr)' : '1fr 1fr',
          gap: space.lg,
        }}
      >
        <AiSummary
          rawnotes={data.rawnotes}
          onEdit={
            canEditNotes
              ? (noteId, current) => setNoteEdit({ noteId, field: 'summary', value: current })
              : undefined
          }
        />
        <OpportunitiesPanel opportunities={data.opportunities} />
      </div>
      {/* Full-width and high, immediately under its own distillation: notes are
          primary content (often the only record of what was actually said), and
          a note's text can run long, so half a grid column would truncate the
          thing we came here to read. Sits directly above Discovery because a
          move note explains why a lead's Discovery answers belong to a
          different conference than the one it is filed under — reading the two
          adjacently is what makes that legible. Below Tasks would bury it. */}
      <NotesPanel
        rawnotes={data.rawnotes}
        onEdit={
          canEditNotes
            ? (noteId, current) => setNoteEdit({ noteId, field: 'text', value: current })
            : undefined
        }
        onAdd={canAddNote ? () => setAddingNote(true) : undefined}
        counter={notesCounterState(data)}
      />
      {/* `key={id}`: DiscoveryPanel owns its own `editing` (question) state with
          no reset tied to `personId` (Task 14). Since neither shell keys the
          `/leads/:id` route by id, this component can be reused across a lead
          change (see the `id`-reset effect above) — without a key here, a
          DiscoveryPanel left mid-edit on the old lead would stay mounted, and
          a Save would fire with the OLD question's conferenceId/questionId
          against the NEW lead's id. The key forces a clean unmount/remount on
          every lead change, so `editing` can never survive one. */}
      <DiscoveryPanel
        key={id}
        personId={id}
        fetchDiscovery={api.fetchDiscovery}
        updateAnswer={
          canUpdateDiscoveryAnswer
            ? (conferenceId, questionId, body) =>
                api.updateDiscoveryAnswer!(conferenceId, id, questionId, body)
            : undefined
        }
        deleteAnswer={
          canDeleteDiscoveryAnswer
            ? (conferenceId, questionId) => api.deleteDiscoveryAnswer!(conferenceId, id, questionId)
            : undefined
        }
      />
      <TasksPanel tasks={data.tasks} />

      {addingNote && (
        <TextEditModal
          open
          title="Add note"
          label="Note text"
          initialValue=""
          // A note body is NOT NULL — blank is not a note.
          saving={savingNewNote}
          onSave={handleCreateNote}
          onCancel={() => setAddingNote(false)}
        />
      )}

      {noteEdit && (
        <TextEditModal
          open
          title={noteEdit.field === 'text' ? 'Edit note' : 'Edit AI summary'}
          label={noteEdit.field === 'text' ? 'Note text' : 'Summary'}
          initialValue={noteEdit.value}
          // A summary is nullable and may legitimately be cleared; note text is NOT NULL.
          allowEmpty={noteEdit.field === 'summary'}
          saving={savingNote}
          onSave={handleSaveNote}
          onCancel={() => setNoteEdit(null)}
        />
      )}
    </div>
  );
}
